import { Button, Checkbox, FormGroup, HTMLSelect } from '@blueprintjs/core'
import React from 'react'
import { useFlexLayout, usePagination, useResizeColumns, useSortBy, useTable } from 'react-table'
import { getSort } from '../utils'
import Spinner from './Spinner'

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 30, 50]

const SELECTION_COLUMN_WIDTH = 40

const SELECTION_COLUMN_STYLE: React.CSSProperties = {
  width: SELECTION_COLUMN_WIDTH,
  minWidth: SELECTION_COLUMN_WIDTH,
  maxWidth: SELECTION_COLUMN_WIDTH
}

const paginationButtonStyle: React.CSSProperties = {
  width: 100,
  textAlign: 'center'
}

type CheckboxTableRow = {
  _id?: string | number
  [key: string]: any
}

interface CheckboxTableProps {
  meta: any
  onMetaChange: (key: string, value: any) => void
  data: any[]
  idKey?: string
  columns: any[]
  loading: boolean
  pageSizeOptions?: number[]
  isSelected?: (key: string | number) => boolean
  toggleSelection?: (key: string | number) => void
  toggleAll?: () => void
  selectAll?: boolean
  [key: string]: any
}

const CheckboxTable = ({
  meta,
  onMetaChange,
  data,
  idKey = 'id',
  columns,
  loading,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  isSelected = () => false,
  toggleSelection = () => {},
  toggleAll = () => {},
  selectAll = false
}: CheckboxTableProps) => {
  const guardedMeta = meta || {}
  const tableData = React.useMemo(
    () =>
      data.map(entry => ({
        _id: entry[idKey],
        ...entry
      })),
    [data, idKey]
  )
  const initialSortBy = React.useMemo(() => {
    const sort = getSort(guardedMeta.sort)
    if (!sort || !sort.length) return []
    return sort.map(s => ({
      id: s.id,
      desc: s.desc
    }))
  }, [guardedMeta.sort])
  const tableColumns = React.useMemo(
    () =>
      columns
        .filter(column => column.show !== false)
        .map(column => ({
          ...column,
          disableSortBy: column.sortable === false
        })),
    [columns]
  )
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, gotoPage, setPageSize, state } =
    useTable<CheckboxTableRow>(
      {
        columns: tableColumns,
        data: tableData,
        manualPagination: true,
        manualSortBy: true,
        pageCount: guardedMeta.maxPages || 1,
        initialState: {
          pageIndex: guardedMeta.page ? guardedMeta.page - 1 : 0,
          pageSize: guardedMeta.perPage || 10,
          sortBy: initialSortBy
        },
        getRowId: row => String(row._id)
      },
      useSortBy,
      usePagination,
      useFlexLayout,
      useResizeColumns
    )
  const { pageIndex, pageSize, sortBy } = state
  React.useEffect(() => {
    if (pageIndex + 1 !== guardedMeta.page) {
      onMetaChange('page', pageIndex + 1)
    }
  }, [guardedMeta.page, onMetaChange, pageIndex])
  React.useEffect(() => {
    if (pageSize !== guardedMeta.perPage) {
      onMetaChange('perPage', pageSize)
    }
  }, [guardedMeta.perPage, onMetaChange, pageSize])
  React.useEffect(() => {
    if (!sortBy.length) return
    const sort = sortBy.map(s => `${s.id}.${s.desc ? 'desc' : 'asc'}`)[0]
    if (sort !== guardedMeta.sort) {
      onMetaChange('sort', sort)
    }
  }, [guardedMeta.sort, onMetaChange, sortBy])

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <div className="listing-manager-table-scroll">
        <div className="ReactTable -striped">
          <table {...getTableProps()} className="rt-table" style={{ width: '100%' }}>
            <thead className="rt-thead -header">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr" key={headerGroup.id}>
                  <th className="rt-th" style={{ ...SELECTION_COLUMN_STYLE, position: 'relative', overflow: 'hidden' }}>
                    <Checkbox checked={selectAll} onChange={toggleAll} />
                  </th>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id}
                      className={[
                        'rt-th',
                        column.headerClassName,
                        column.isSorted && (column.isSortedDesc ? '-sort-desc' : '-sort-asc'),
                        column.canSort && '-cursor-pointer'
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ ...column.getHeaderProps().style, position: 'relative', overflow: 'hidden' }}
                    >
                      {column.render('Header')}
                      {column.canResize ? (
                        <div
                          {...column.getResizerProps()}
                          onClick={e => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: 8,
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            background: column.isResizing ? '#2196f3' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className="rt-tbody">
              {page.map((row, rowIndex) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} className={`rt-tr ${rowIndex % 2 === 0 ? '-odd' : '-even'}`} key={row.id}>
                    <td className="rt-td" style={{ ...SELECTION_COLUMN_STYLE, overflow: 'hidden' }}>
                      <Checkbox
                        checked={isSelected(row.original._id)}
                        onChange={() => toggleSelection(row.original._id)}
                      />
                    </td>
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className="rt-td"
                        key={cell.column.id}
                        style={{ ...cell.getCellProps().style, overflow: 'hidden' }}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="listing-manager-table-pagination">
        <Button large style={paginationButtonStyle} onClick={() => gotoPage(0)} disabled={(guardedMeta.page || 1) <= 1}>
          First
        </Button>{' '}
        <Button
          large
          style={paginationButtonStyle}
          onClick={() => gotoPage((guardedMeta.page || 1) - 2)}
          disabled={(guardedMeta.page || 1) <= 1}
        >
          Previous
        </Button>{' '}
        <span>
          Page {guardedMeta.page || 1} of {guardedMeta.maxPages || 1}
        </span>{' '}
        <FormGroup inline label="Page size">
          <HTMLSelect
            options={pageSizeOptions.map(size => ({ value: size }))}
            value={guardedMeta.perPage || 10}
            onChange={e => setPageSize(Number(e.target.value))}
          />
        </FormGroup>
        <Button
          large
          style={paginationButtonStyle}
          onClick={() => gotoPage(guardedMeta.page || 1)}
          disabled={(guardedMeta.page || 1) >= (guardedMeta.maxPages || 1)}
        >
          Next
        </Button>{' '}
        <Button
          large
          style={paginationButtonStyle}
          onClick={() => gotoPage((guardedMeta.maxPages || 1) - 1)}
          disabled={(guardedMeta.page || 1) >= (guardedMeta.maxPages || 1)}
        >
          Last
        </Button>
      </div>
    </>
  )
}

export { CheckboxTable }
export default React.memo(CheckboxTable)
